/* eslint-disable no-param-reassign */
import {
  CategoryScale, Chart as ChartJS, Filler, LinearScale, LineElement, PointElement, Tooltip,
} from 'chart.js';
import moment from 'moment';
import React from 'react';
import { Line } from 'react-chartjs-2';
import { IAgentLandingSeriesPoint } from 'types/agent-landing-page';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const colors = {
  orange: '#f26430', green: '#14765c', ink: '#102a2b', grid: '#dce3de', muted: '#778682', paper: '#fffdfa',
};
const compactCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1,
});
const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Altos returns weekly snapshots. Charts in the design show monthly states, so
 * each month uses its final available weekly observation rather than a sum. */
export const aggregateMonthlyLatest = (series: IAgentLandingSeriesPoint[], limit = 6) => {
  const byMonth = new Map<string, IAgentLandingSeriesPoint>();
  [...series].sort((a, b) => a.date.localeCompare(b.date)).forEach((point) => {
    const key = moment(point.date).format('YYYY-MM');
    const current = byMonth.get(key);
    if (!current || point.date > current.date) byMonth.set(key, point);
  });
  return [...byMonth.values()].sort((a, b) => a.date.localeCompare(b.date)).slice(-limit);
};

const chartOptions = (
  formatValue: (value: number) => string,
  compact = false,
  hideYAxis = false,
  tooltipEnabled = true,
  endLabelGutter = 0,
) => ({
  responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: typeof window === 'undefined' ? 2 : Math.min((window.devicePixelRatio || 1) * 2, 4),
  animation: { duration: prefersReducedMotion ? 0 : 350 },
  interaction: { intersect: false, mode: 'index' as const },
  layout: {
    padding: compact ? {
      top: 8, right: 4, bottom: 0, left: 0,
    } : {
      top: 28, right: 8 + endLabelGutter, bottom: 0, left: 0,
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: tooltipEnabled,
      backgroundColor: colors.ink,
      titleColor: colors.paper,
      bodyColor: colors.paper,
      borderColor: 'rgba(255,255,255,.14)',
      borderWidth: 1,
      padding: 10,
      displayColors: true,
      titleFont: { family: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 10, weight: '600' as const },
      bodyFont: { family: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
      callbacks: { label: (context: any) => `${context.dataset.label}: ${formatValue(context.parsed.y)}` },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: colors.muted, maxRotation: 0, font: { family: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 9, weight: '500' as const } },
    },
    y: {
      display: !hideYAxis,
      grid: { color: colors.grid, drawTicks: false },
      border: { display: false },
      ticks: {
        color: colors.muted, padding: 9, maxTicksLimit: compact ? 3 : 4, callback: (value: any) => formatValue(Number(value)), font: { family: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 9 },
      },
    },
  },
});

const labelsFor = (series: IAgentLandingSeriesPoint[]) => series.map((point) => moment(point.date).format('MMM').toUpperCase());

const lastDefinedIndex = (data: unknown[]) => {
  for (let index = data.length - 1; index >= 0; index -= 1) {
    if (typeof data[index] === 'number' && Number.isFinite(data[index])) return index;
  }
  return -1;
};

const finalValueLabels = {
  id: 'agentLandingFinalValueLabels',
  afterDatasetsDraw: (chart: any) => {
    const { ctx } = chart;
    const labels = chart.data.datasets.reduce((placements: any[], dataset: any, datasetIndex: number) => {
      const finalIndex = lastDefinedIndex(dataset.data);
      const point = chart.getDatasetMeta(datasetIndex).data[finalIndex];
      const value = dataset.data[finalIndex];
      if (point && typeof value === 'number') {
        placements.push({
          color: dataset.borderColor, point, value, y: point.y,
        });
      }
      return placements;
    }, []).sort((a: any, b: any) => a.y - b.y);
    if (!labels.length) return;

    const minimumGap = 18;
    labels.forEach((label: any, index: number) => {
      if (index > 0) label.y = Math.max(label.y, labels[index - 1].y + minimumGap);
    });

    const bottomOverflow = labels[labels.length - 1]?.y - (chart.chartArea.bottom - 8);
    if (bottomOverflow > 0) labels.forEach((label: any) => { label.y -= bottomOverflow; });
    const topOverflow = (chart.chartArea.top + 8) - labels[0]?.y;
    if (topOverflow > 0) labels.forEach((label: any) => { label.y += topOverflow; });

    labels.forEach((label: any) => {
      ctx.save();
      ctx.fillStyle = label.color;
      ctx.font = '600 11px Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(compactCurrency.format(label.value), label.point.x + 9, label.y);
      ctx.restore();
    });
  },
};

export const PricingChart = ({ list, newListings }: { list: IAgentLandingSeriesPoint[]; newListings: IAgentLandingSeriesPoint[] }) => {
  const monthlyList = aggregateMonthlyLatest(list);
  const monthlyNew = aggregateMonthlyLatest(newListings);
  const newByMonth = new Map(monthlyNew.map((point) => [moment(point.date).format('YYYY-MM'), point.value]));
  const endpointRadius = (context: any) => (context.dataIndex === lastDefinedIndex(context.dataset.data) ? 5 : 0);
  return (
    <figure className="al-chart al-pricing-chart" role="img" aria-label="Six-month chart of median list and median new-listing asking prices">
      <Line
        data={{
          labels: labelsFor(monthlyList),
          datasets: [
            {
              label: 'Median list', data: monthlyList.map((point) => point.value), borderColor: colors.orange, backgroundColor: 'rgba(242,100,48,.14)', fill: true, tension: 0.34, pointRadius: endpointRadius, pointHoverRadius: endpointRadius, pointBorderWidth: 0, pointBackgroundColor: colors.orange, borderWidth: 4,
            },
            {
              label: 'Median new listing', data: monthlyList.map((point) => newByMonth.get(moment(point.date).format('YYYY-MM')) ?? null), borderColor: colors.green, backgroundColor: 'transparent', tension: 0.34, pointRadius: endpointRadius, pointHoverRadius: endpointRadius, pointBorderWidth: 0, pointBackgroundColor: colors.green, borderCapStyle: 'round', borderJoinStyle: 'round', borderDash: [8, 8], borderWidth: 4,
            },
          ],
        }}
        options={chartOptions((value) => compactCurrency.format(value), false, false, false, 52)}
        plugins={[finalValueLabels]}
      />
    </figure>
  );
};

export const InventoryChart = ({ series }: { series: IAgentLandingSeriesPoint[] }) => {
  const monthly = aggregateMonthlyLatest(series);
  const maxValue = Math.max(...monthly.map((point) => point.value), 1);
  return (
    <figure className="al-inventory-bars" role="img" aria-label="Six-month chart of active residential inventory">
      {monthly.map((point, index) => (
        <div
          key={point.date}
          className={index === monthly.length - 1 ? 'current' : undefined}
          style={{ '--h': `${Math.max(8, (point.value / maxValue) * 94)}%` } as React.CSSProperties}
          title={`${moment(point.date).format('MMMM YYYY')}: ${Math.round(point.value).toLocaleString()} active homes`}
        >
          <b>{Math.round(point.value).toLocaleString()}</b>
          <i>{moment(point.date).format('MMM')}</i>
        </div>
      ))}
    </figure>
  );
};

export const RentChart = ({ series }: { series: IAgentLandingSeriesPoint[] }) => {
  const monthly = aggregateMonthlyLatest(series);
  return (
    <figure className="al-chart al-rent-chart" role="img" aria-label="Six-month chart of median asking rent">
      <Line
        data={{
          labels: labelsFor(monthly),
          datasets: [{
            label: 'Median asking rent', data: monthly.map((point) => point.value), borderColor: colors.green, backgroundColor: 'rgba(20,118,92,.1)', fill: true, tension: 0.34, pointRadius: 0, pointHoverRadius: 0, borderWidth: 4,
          }],
        }}
        options={chartOptions((value) => compactCurrency.format(value), true, false, false)}
      />
    </figure>
  );
};
